import React, { useEffect, useState } from 'react';
import { Package, Search, Calendar, CheckCircle, Truck, Clock, Star, RefreshCcw, X, Upload } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

const CustomerOrders = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [myReturns, setMyReturns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Modal States
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    
    // Review Form State
    const [rating, setRating] = useState(5);
    const [reviewMsg, setReviewMsg] = useState('');
    
    // Return Form State
    const [returnReason, setReturnReason] = useState('Damaged plant');
    const [returnDesc, setReturnDesc] = useState('');
    const [returnImage, setReturnImage] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchOrdersAndReturns = async () => {
        try {
            let token = localStorage.getItem('token') || sessionStorage.getItem('token');
            if (!token) {
                const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
                token = userInfo.token;
            }

            if (!token) {
                toast.error("Please login to view your orders");
                setLoading(false);
                return navigate('/account');
            }
            
            const [ordersRes, returnsRes] = await Promise.all([
                axios.get('/api/orders/myorders', { headers: { Authorization: `Bearer ${token}` } }),
                axios.get('/api/returns/myreturns', { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { returns: [] } }))
            ]);
            
            setOrders(ordersRes.data.orders || []);
            setMyReturns(returnsRes.data.returns || []);
        } catch (error) {
            console.error('Error fetching orders:', error);
            toast.error('Failed to load orders');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrdersAndReturns();
    }, []);

    const handleReviewSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            let token = localStorage.getItem('token') || sessionStorage.getItem('token');
            await axios.post('/api/reviews', {
                productId: selectedItem.product._id,
                rating,
                message: reviewMsg
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Review submitted successfully!');
            setShowReviewModal(false);
            setReviewMsg('');
            setRating(5);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to submit review');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReturnSubmit = async (e) => {
        e.preventDefault();
        if (!returnImage) {
            return toast.error("Please upload an image of the damaged product");
        }
        setIsSubmitting(true);
        try {
            let token = localStorage.getItem('token') || sessionStorage.getItem('token');
            
            // 1. Upload Image
            const formData = new FormData();
            formData.append('image', returnImage);
            const uploadRes = await axios.post('/api/upload', formData, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            // 2. Submit Return
            await axios.post('/api/returns', {
                orderId: selectedOrder._id,
                productId: selectedItem.product._id,
                reason: returnReason,
                description: returnDesc,
                image: uploadRes.data.url
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success('Exchange request submitted successfully!');
            setShowReturnModal(false);
            setReturnImage(null);
            setReturnDesc('');
            fetchOrdersAndReturns(); // Refresh to show pending return
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to submit return request');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStatusInfo = (status) => {
        switch (status) {
            case 'Delivered': return { label: 'Delivered', color: '#10b981', icon: CheckCircle };
            case 'Shipped': return { label: 'Shipped', color: '#3b82f6', icon: Truck };
            case 'Processing': return { label: 'Processing', color: '#f59e0b', icon: Clock };
            default: return { label: status || 'Processing', color: '#64748b', icon: Package };
        }
    };

    const filteredOrders = orders.filter(order => 
        order._id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.orderItems?.some(item => item.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (loading) {
        return (
            <div className="orders-loading">
                <div className="loader"></div>
            </div>
        );
    }

    return (
        <div className="customer-orders">
            {/* Header */}
            <div className="orders-header">
                <h2>My Orders</h2>
                <div className="search-box">
                    <Search size={16} />
                    <input 
                        type="text" 
                        placeholder="Search orders..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Orders List */}
            {filteredOrders.length === 0 ? (
                <div className="empty-orders">
                    <Package size={48} />
                    <h3>No orders yet</h3>
                    <p>Start shopping to see your orders here</p>
                    <Link to="/shop" className="shop-link">Shop Now →</Link>
                </div>
            ) : (
                <div className="orders-list">
                    {filteredOrders.map((order) => {
                        const statusInfo = getStatusInfo(order.status);
                        const StatusIcon = statusInfo.icon;
                        
                        return (
                            <div key={order._id} className="order-card">
                                <div className="order-header">
                                    <div className="order-meta">
                                        <span className="order-date">
                                            <Calendar size={14} />
                                            {new Date(order.createdAt).toLocaleDateString('en-US', { 
                                                month: 'short', day: 'numeric', year: 'numeric' 
                                            })}
                                        </span>
                                        <span className="order-id">#{order._id.slice(-8).toUpperCase()}</span>
                                    </div>
                                    <div className="order-total">₹{order.totalAmount?.toLocaleString() || '0'}</div>
                                </div>

                                <div className="order-items">
                                    {order.orderItems?.map((item, idx) => {
                                        const returnReq = myReturns.find(r => r.order === order._id && r.product?._id === item.product?._id);
                                        return (
                                            <div key={idx} className="order-item">
                                                <Link to={`/product/${item.product?._id}`}>
                                                    <img 
                                                        src={item.product?.image || '/assets/images/thumbs/plant-placeholder.png'} 
                                                        alt={item.name}
                                                        onError={(e) => { e.target.src = '/assets/images/thumbs/plant-placeholder.png'; }}
                                                    />
                                                </Link>
                                                <div className="item-details" style={{ flex: 1 }}>
                                                    <Link to={`/product/${item.product?._id}`} className="item-name" style={{ textDecoration: 'none' }}>
                                                        {item.name}
                                                    </Link>
                                                    <span className="item-qty">Qty: {item.quantity}</span>
                                                    
                                                    {order.status === 'Delivered' && (
                                                        <div className="item-actions">
                                                            <button 
                                                                className="action-btn-small"
                                                                onClick={() => {
                                                                    setSelectedItem(item);
                                                                    setShowReviewModal(true);
                                                                }}
                                                            >
                                                                <Star size={12} /> Give Rating
                                                            </button>
                                                            
                                                            {returnReq ? (
                                                                <span className="return-status-badge">
                                                                    <RefreshCcw size={12} /> Exchange {returnReq.status}
                                                                </span>
                                                            ) : (
                                                                <button 
                                                                    className="action-btn-small outline"
                                                                    onClick={() => {
                                                                        setSelectedItem(item);
                                                                        setSelectedOrder(order);
                                                                        setShowReturnModal(true);
                                                                    }}
                                                                >
                                                                    <RefreshCcw size={12} /> Request Exchange
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="order-footer">
                                    <div className="order-status" style={{ color: statusInfo.color }}>
                                        <StatusIcon size={14} />
                                        <span>{statusInfo.label}</span>
                                    </div>
                                    <Link to={`/account/orders/${order._id}`} className="view-btn">
                                        View Details →
                                    </Link>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Review Modal */}
            {showReviewModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>Write a Review</h3>
                            <button className="close-btn" onClick={() => setShowReviewModal(false)}><X size={20} /></button>
                        </div>
                        <form onSubmit={handleReviewSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label>Rating</label>
                                    <div className="star-rating">
                                        {[1, 2, 3, 4, 5].map(star => (
                                            <Star 
                                                key={star} 
                                                size={24} 
                                                onClick={() => setRating(star)}
                                                fill={rating >= star ? '#f59e0b' : 'none'}
                                                color={rating >= star ? '#f59e0b' : '#cbd5e1'}
                                                style={{ cursor: 'pointer' }}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Your Review</label>
                                    <textarea 
                                        rows="4" 
                                        value={reviewMsg}
                                        onChange={(e) => setReviewMsg(e.target.value)}
                                        placeholder="What did you like or dislike? What did you use this product for?"
                                        required
                                    ></textarea>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="cancel-btn" onClick={() => setShowReviewModal(false)}>Cancel</button>
                                <button type="submit" className="submit-btn" disabled={isSubmitting}>
                                    {isSubmitting ? 'Submitting...' : 'Submit Review'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Return Modal */}
            {showReturnModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>Exchange Product</h3>
                            <button className="close-btn" onClick={() => setShowReturnModal(false)}><X size={20} /></button>
                        </div>
                        <form onSubmit={handleReturnSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label>Reason for Exchange</label>
                                    <select value={returnReason} onChange={(e) => setReturnReason(e.target.value)}>
                                        <option value="Damaged plant">Damaged Plant / Dead on Arrival</option>
                                        <option value="Wrong item delivered">Wrong Item Delivered</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Description</label>
                                    <textarea 
                                        rows="3" 
                                        value={returnDesc}
                                        onChange={(e) => setReturnDesc(e.target.value)}
                                        placeholder="Please provide details about the issue..."
                                        required
                                    ></textarea>
                                </div>
                                <div className="form-group">
                                    <label>Upload Photo of Product (Required)</label>
                                    <div className="image-upload-box">
                                        <input 
                                            type="file" 
                                            accept="image/*" 
                                            onChange={(e) => setReturnImage(e.target.files[0])}
                                            id="return-img"
                                            style={{ display: 'none' }}
                                        />
                                        <label htmlFor="return-img" className="upload-label">
                                            <Upload size={24} />
                                            <span>{returnImage ? returnImage.name : 'Click to select image'}</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="cancel-btn" onClick={() => setShowReturnModal(false)}>Cancel</button>
                                <button type="submit" className="submit-btn" disabled={isSubmitting}>
                                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                .customer-orders {
                    max-width: 100%;
                }

                .orders-loading {
                    display: flex;
                    justify-content: center;
                    padding: 40px;
                }

                .loader {
                    width: 32px;
                    height: 32px;
                    border: 2px solid #e2e8f0;
                    border-top-color: #10b981;
                    border-radius: 50%;
                    animation: spin 0.6s linear infinite;
                }

                @keyframes spin {
                    to { transform: rotate(360deg); }
                }

                .orders-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                    flex-wrap: wrap;
                    gap: 16px;
                }

                .orders-header h2 {
                    font-size: 20px;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0;
                }

                .search-box {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 16px;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 40px;
                }

                .search-box input {
                    border: none;
                    outline: none;
                    font-size: 13px;
                    width: 200px;
                }

                .search-box svg {
                    color: #94a3b8;
                }

                .orders-list {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .order-card {
                    background: white;
                    border-radius: 16px;
                    border: 1px solid #e8ecef;
                    overflow: hidden;
                }

                .order-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 14px 20px;
                    background: #f8fafc;
                    border-bottom: 1px solid #e8ecef;
                }

                .order-meta {
                    display: flex;
                    gap: 20px;
                    align-items: center;
                }

                .order-date {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 12px;
                    color: #64748b;
                }

                .order-id {
                    font-size: 12px;
                    font-weight: 500;
                    color: #1e293b;
                }

                .order-total {
                    font-weight: 700;
                    font-size: 14px;
                    color: #10b981;
                }

                .order-items {
                    padding: 16px 20px;
                }

                .order-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 12px;
                    margin-bottom: 16px;
                    padding-bottom: 16px;
                    border-bottom: 1px dashed #e2e8f0;
                }

                .order-item:last-child {
                    margin-bottom: 0;
                    padding-bottom: 0;
                    border-bottom: none;
                }

                .order-item img {
                    width: 60px;
                    height: 60px;
                    border-radius: 8px;
                    object-fit: cover;
                }

                .item-details {
                    display: flex;
                    flex-direction: column;
                }

                .item-name {
                    font-size: 14px;
                    font-weight: 500;
                    color: #1e293b;
                }
                
                .item-name:hover {
                    color: #10b981;
                }

                .item-qty {
                    font-size: 12px;
                    color: #94a3b8;
                    margin-top: 2px;
                }

                .item-actions {
                    display: flex;
                    gap: 10px;
                    margin-top: 10px;
                }

                .action-btn-small {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    padding: 4px 10px;
                    background: #fef3c7;
                    color: #d97706;
                    border: none;
                    border-radius: 4px;
                    font-size: 11px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: 0.2s;
                }
                .action-btn-small:hover { background: #fde68a; }

                .action-btn-small.outline {
                    background: transparent;
                    border: 1px solid #cbd5e1;
                    color: #64748b;
                }
                .action-btn-small.outline:hover { background: #f8fafc; color: #1e293b; }

                .return-status-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    padding: 4px 10px;
                    background: #f1f5f9;
                    color: #475569;
                    border-radius: 4px;
                    font-size: 11px;
                    font-weight: 600;
                }

                .order-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 12px 20px;
                    border-top: 1px solid #e8ecef;
                    background: white;
                }

                .order-status {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 12px;
                    font-weight: 500;
                }

                .view-btn {
                    font-size: 12px;
                    font-weight: 500;
                    color: #10b981;
                    text-decoration: none;
                }

                .view-btn:hover {
                    text-decoration: underline;
                }

                .empty-orders {
                    text-align: center;
                    padding: 60px 20px;
                    background: white;
                    border-radius: 16px;
                    border: 1px solid #e8ecef;
                }

                .empty-orders svg {
                    color: #94a3b8;
                    margin-bottom: 16px;
                }

                .empty-orders h3 {
                    font-size: 16px;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0 0 6px;
                }

                .empty-orders p {
                    font-size: 13px;
                    color: #64748b;
                    margin: 0 0 20px;
                }

                .shop-link {
                    display: inline-block;
                    padding: 10px 24px;
                    background: #10b981;
                    color: white;
                    border-radius: 40px;
                    text-decoration: none;
                    font-size: 13px;
                    font-weight: 500;
                }

                /* Modals */
                .modal-overlay {
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0,0,0,0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                }
                .modal-content {
                    background: white;
                    width: 100%;
                    max-width: 450px;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
                }
                .modal-header {
                    padding: 16px 20px;
                    border-bottom: 1px solid #e2e8f0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .modal-header h3 { margin: 0; font-size: 16px; }
                .close-btn { background: none; border: none; cursor: pointer; color: #64748b; }
                
                .modal-body { padding: 20px; }
                .form-group { margin-bottom: 16px; }
                .form-group label { display: block; margin-bottom: 6px; font-size: 13px; font-weight: 500; color: #475569; }
                .form-group select, .form-group textarea {
                    width: 100%; padding: 10px; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 14px;
                }
                .star-rating { display: flex; gap: 4px; }
                
                .image-upload-box { margin-top: 8px; }
                .upload-label {
                    display: flex; flex-direction: column; align-items: center; gap: 8px;
                    padding: 20px; border: 2px dashed #cbd5e1; border-radius: 8px;
                    cursor: pointer; color: #64748b; font-size: 13px;
                    transition: 0.2s;
                }
                .upload-label:hover { border-color: #10b981; color: #10b981; }
                
                .modal-footer {
                    padding: 16px 20px; border-top: 1px solid #e2e8f0;
                    display: flex; justify-content: flex-end; gap: 12px; background: #f8fafc;
                }
                .cancel-btn { padding: 8px 16px; background: white; border: 1px solid #cbd5e1; border-radius: 6px; cursor: pointer; font-weight: 500;}
                .submit-btn { padding: 8px 16px; background: #10b981; color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 500;}
                .submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }

                @media (max-width: 600px) {
                    .orders-header { flex-direction: column; align-items: stretch; }
                    .search-box input { width: 100%; }
                    .order-header { flex-direction: column; align-items: flex-start; gap: 8px; }
                    .order-meta { flex-wrap: wrap; }
                }
            `}</style>
        </div>
    );
};

export default CustomerOrders;